import { pitchShift, psolaShift, diatonicSemis, trackPitch, seedOf, makeImpulse, encodeWav, trimStart } from "./dsp";
import { api } from "@/lib/api";

const set = (param, v, ctx, instant) =>
  instant ? (param.value = v) : param.setTargetAtTime(v, ctx.currentTime, 0.02);

const anySolo = (tracks) => tracks.some((t) => t.solo);
const audible = (t, solo) => !t.mute && (!solo || t.solo);

function newConvolver(bus, size) {
  const conv = bus.ctx.createConvolver();
  conv.buffer = makeImpulse(bus.ctx, size);
  bus.reverbIn.connect(conv);
  conv.connect(bus.out);
  if (bus.reverb) {
    bus.reverbIn.disconnect(bus.reverb);
    bus.reverb.disconnect();
  }
  bus.reverb = conv;
  bus.size = size;
}

function applyMaster(bus, m, instant) {
  set(bus.out.gain, m.volume, bus.ctx, instant);
  set(bus.delay.delayTime, m.delay_time, bus.ctx, instant);
  set(bus.fb.gain, m.delay_feedback, bus.ctx, instant);
  if (bus.size !== m.reverb_size) newConvolver(bus, m.reverb_size);
}

function buildBus(ctx, master) {
  const out = ctx.createGain();
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -2;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.2;
  out.connect(limiter).connect(ctx.destination);
  const reverbIn = ctx.createGain();
  const delayIn = ctx.createGain();
  const delay = ctx.createDelay(2);
  const fb = ctx.createGain();
  const tone = ctx.createBiquadFilter();
  tone.type = "lowpass";
  tone.frequency.value = 3800;
  delayIn.connect(delay);
  delay.connect(tone);
  tone.connect(fb).connect(delay);
  tone.connect(out);
  const bus = { ctx, out, limiter, reverbIn, delayIn, delay, fb, reverb: null, size: null };
  applyMaster(bus, master, true);
  return bus;
}

function buildChain(ctx, bus) {
  const input = ctx.createGain();
  const low = ctx.createBiquadFilter();
  low.type = "lowshelf";
  low.frequency.value = 120;
  const mid = ctx.createBiquadFilter();
  mid.type = "peaking";
  mid.frequency.value = 1000;
  mid.Q.value = 0.9;
  const high = ctx.createBiquadFilter();
  high.type = "highshelf";
  high.frequency.value = 8000;
  const fader = ctx.createGain();
  const pan = ctx.createStereoPanner();
  const rev = ctx.createGain();
  const del = ctx.createGain();
  input.connect(low).connect(mid).connect(high).connect(fader).connect(pan);
  pan.connect(bus.out);
  pan.connect(rev).connect(bus.reverbIn);
  pan.connect(del).connect(bus.delayIn);
  return { ctx, input, low, mid, high, fader, pan, rev, del };
}

function applyTrack(c, t, solo, instant) {
  set(c.fader.gain, audible(t, solo) ? t.volume : 0, c.ctx, instant);
  set(c.pan.pan, t.pan, c.ctx, instant);
  set(c.low.gain, t.eq_low, c.ctx, instant);
  set(c.mid.gain, t.eq_mid, c.ctx, instant);
  set(c.high.gain, t.eq_high, c.ctx, instant);
  set(c.rev.gain, t.reverb, c.ctx, instant);
  set(c.del.gain, t.delay, c.ctx, instant);
}

class AudioEngine {
  ctx = null;
  bus = null;
  raw = new Map();
  pitch = new Map();
  loop = null;
  loopTimer = null;
  clickTimer = null;
  processed = new Map();
  chains = new Map();
  sources = [];
  playing = false;
  startAt = 0;
  offsetAt = 0;
  project = null;

  ensure() {
    if (!this.ctx) this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    return this.ctx;
  }

  reset() {
    this.stop();
    this.chains.forEach((c) => c.pan.disconnect());
    this.chains.clear();
    this.processed.clear();
    if (this.bus) this.bus.limiter.disconnect();
    this.bus = null;
  }

  sync(project) {
    const ctx = this.ensure();
    this.project = project;
    if (!this.bus) this.bus = buildBus(ctx, project.master);
    else applyMaster(this.bus, project.master, false);
    const solo = anySolo(project.tracks);
    const ids = new Set(project.tracks.map((t) => t.id));
    project.tracks.forEach((t) => {
      if (!this.chains.has(t.id)) this.chains.set(t.id, buildChain(ctx, this.bus));
      applyTrack(this.chains.get(t.id), t, solo, false);
    });
    [...this.chains.keys()].forEach((id) => {
      if (!ids.has(id)) {
        this.chains.get(id).pan.disconnect();
        this.chains.delete(id);
        this.processed.delete(id);
      }
    });
  }

  cacheRaw(fileId, buffer) {
    this.raw.set(fileId, buffer);
  }

  async loadTrack(t, meta) {
    const ctx = this.ensure();
    if (!this.raw.has(t.file_id)) {
      const ab = await api.fetchAudio(t.file_id);
      this.raw.set(t.file_id, await ctx.decodeAudioData(ab));
    }
    await new Promise((r) => setTimeout(r, 0));
    const base = trimStart(ctx, this.raw.get(t.file_id), t.trim || 0);
    const diatonic = t.mode === "diatonic";
    let buf = base;
    if (t.kind === "backing") buf = pitchShift(ctx, base, t.semitones);
    else if (diatonic ? t.degrees : t.semitones) {
      const key = `${t.file_id}:${t.trim || 0}`;
      if (!this.pitch.has(key)) this.pitch.set(key, trackPitch(base));
      const pitch = this.pitch.get(key);
      const semisAt = diatonic ? diatonicSemis(pitch, meta.key, meta.scale, t.degrees) : () => t.semitones;
      buf = psolaShift(ctx, base, pitch, semisAt, seedOf(t.id));
    }
    this.processed.set(t.id, buf);
  }

  durationOf(tracks) {
    let d = 0;
    tracks.forEach((t) => {
      const b = this.processed.get(t.id);
      if (b) d = Math.max(d, (t.start || 0) + b.duration);
    });
    return d;
  }

  get duration() {
    return this.durationOf(this.project?.tracks || []);
  }

  blip(t, accent) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.frequency.value = accent ? 1760 : 1175;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(accent ? 0.55 : 0.35, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
    osc.connect(g).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.1);
  }

  countIn(bpm, beats = 4) {
    const ctx = this.ensure();
    ctx.resume();
    const beat = 60 / bpm;
    const t0 = ctx.currentTime + 0.05;
    for (let i = 0; i < beats; i++) this.blip(t0 + i * beat, i === 0);
    return beats * beat;
  }

  // Continuous click on the song's beat grid; origin = context time of timeline 0
  startClick(bpm, origin) {
    this.stopClick();
    const ctx = this.ensure();
    const beat = 60 / bpm;
    let k = Math.ceil((ctx.currentTime - origin) / beat - 1e-6);
    const tick = () => {
      while (origin + k * beat < ctx.currentTime + 0.15) {
        const t = origin + k * beat;
        if (t >= ctx.currentTime) this.blip(t, ((k % 4) + 4) % 4 === 0);
        k++;
      }
    };
    tick();
    this.clickTimer = setInterval(tick, 40);
  }

  stopClick() {
    clearInterval(this.clickTimer);
    this.clickTimer = null;
  }

  scheduleSegment(when, from, to) {
    const ctx = this.ctx;
    (this.project?.tracks || []).forEach((t) => {
      const buf = this.processed.get(t.id);
      const chain = this.chains.get(t.id);
      if (!buf || !chain) return;
      const ts = t.start || 0;
      const a = Math.max(from, ts);
      const b = Math.min(to, ts + buf.duration);
      if (b - a <= 0.001) return;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(chain.input);
      src.start(when + (a - from), a - ts, b - a);
      src.onended = () => (this.sources = this.sources.filter((s) => s !== src));
      this.sources.push(src);
    });
  }

  play(offset, lead = 0, loop = null) {
    const ctx = this.ensure();
    ctx.resume();
    this.stopSources();
    const t0 = ctx.currentTime + 0.05 + lead;
    this.loop = loop && loop.end - loop.start > 0.2 ? loop : null;
    if (this.loop && (offset < loop.start || offset >= loop.end)) offset = loop.start;
    this.startAt = t0;
    this.offsetAt = offset;
    this.playing = true;
    if (!this.loop) return this.scheduleSegment(t0, offset, Infinity);
    const L = loop.end - loop.start;
    this.scheduleSegment(t0, offset, loop.end);
    let next = t0 + (loop.end - offset);
    const pump = () => {
      while (next < ctx.currentTime + 0.6) {
        this.scheduleSegment(next, loop.start, loop.end);
        next += L;
      }
    };
    pump();
    this.loopTimer = setInterval(pump, 150);
  }

  get latency() {
    const ctx = this.ensure();
    return 0.05 + (ctx.outputLatency || 0) + (ctx.baseLatency || 0);
  }

  getTime() {
    if (!this.playing) return this.offsetAt;
    const el = this.ctx.currentTime - this.startAt;
    if (el < 0) return this.offsetAt;
    if (!this.loop) return this.offsetAt + el;
    const first = this.loop.end - this.offsetAt;
    if (el < first) return this.offsetAt + el;
    return this.loop.start + ((el - first) % (this.loop.end - this.loop.start));
  }

  pause() {
    const t = this.getTime();
    this.stopSources();
    this.playing = false;
    this.offsetAt = t;
    return t;
  }

  stop() {
    this.stopSources();
    this.stopClick();
    this.playing = false;
    this.offsetAt = 0;
  }

  stopSources() {
    clearInterval(this.loopTimer);
    this.loopTimer = null;
    this.loop = null;
    [...this.sources].forEach((s) => {
      s.onended = null;
      try {
        s.stop();
      } catch (e) {
        /* already stopped */
      }
      s.disconnect();
    });
    this.sources = [];
  }

  async exportWav(project, vocalsOnly = false) {
    return encodeWav(await this.renderMix(project, vocalsOnly));
  }

  async renderMix(project, vocalsOnly = false, sr = 44100) {
    const tracks = vocalsOnly ? project.tracks.filter((t) => t.kind !== "backing") : project.tracks;
    const off = new OfflineAudioContext(2, Math.ceil(sr * (this.durationOf(tracks) + project.master.reverb_size)), sr);
    const bus = buildBus(off, project.master);
    const solo = anySolo(tracks);
    tracks.forEach((t) => {
      const buf = this.processed.get(t.id);
      if (!buf) return;
      const chain = buildChain(off, bus);
      applyTrack(chain, t, solo, true);
      const src = off.createBufferSource();
      src.buffer = buf;
      src.connect(chain.input);
      src.start(t.start || 0);
    });
    return off.startRendering();
  }
}

export const engine = new AudioEngine();
